import BenchlingConfig from 'BenchlingConfig';
import {LeafType, isBenchlingTypeLeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import inferTypeFromLookupConfig, {
  InferredLookupConfigType,
  LookupSchemaFieldNotFoundError,
  LookupSchemaNotFoundError,
} from 'lookup-config/inferTypeFromLookupConfig';
import {StructuredTableSchema} from 'notebook/structured-tables/StructuredTableConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {ParentStorableType} from 'samples/storables/StorableSchemaConstants';
import {
  FieldType,
  getRequiredLinkTagSchema,
  isRequiredLinkCustomSchemaInterface,
  isRequiredLinkStorableSchema,
  isRequiredLinkTagSchema,
} from 'schemas/fields/SchemaFieldConstants';
import APIUtil from 'util/APIUtil';
import {assertNever} from 'util/Types';

import {CustomColumnDataType} from '../custom-columns/CustomColumnConstants';
import {ColumnDefinition} from './ColumnDefinitions';

/**
 * Describes what type of data a column is likely to contain.
 *
 * This "column data type" concept is distinct from both:
 * - The column's TableColumnType, which often has more to do with the column's "role" in the table
 *   than what type of data it contains
 * - The TypedValueType of the column's contents, which doesn't always match up cleanly with Benchling's standard data types.
 *   For example, Benchling entities can be represented both as a BIOENTITY typed value and as a ft_file_link typed value.
 *
 * ColumnDataTypeSpec is similar to the SampleFilter interface used by our UAS subsystem,
 * but we can keep them distinct for now, as they currently serve slightly different purposes.
 * Neither ColumnDataTypeSpec nor SampleFilter are persisted, so they should be easy enough to unify
 * if it makes sense to do so.
 */
export interface ColumnDataTypeSpec {
  type: ColumnDataType;
  schemaId?: string | null;
  schemaInterfaceId?: string | null;
  isMulti?: boolean; // assume false if not included
}

export enum ColumnDataType {
  // primitive
  TEXT = 'text',
  INTEGER = 'integer',
  DECIMAL = 'decimal',
  BOOLEAN = 'boolean',
  DATE = 'date',
  DATETIME = 'datetime',
  // objects
  ENTRY = 'entry',
  ATTACHMENT = 'attachment',
  // schematized
  ENTITY = 'entity',
  CONTAINER = 'container',
  LOCATION = 'location',
  DROPDOWN = 'dropdown',
  PLATE = 'plate',
  BOX = 'box',
  STORABLE = 'storable',
  // workflows
  PROCESS_TASK = 'process_task',
  // delegated to rows
  ROW_DELEGATE = 'row_delegate',
}

function getStorableLinkColumnTypeFromSchema(schemaId: string | null): ColumnDataType {
  if (schemaId == null) {
    return ColumnDataType.STORABLE;
  } else if (APIUtil.isContainerSchema(schemaId)) {
    return ColumnDataType.CONTAINER;
  } else if (APIUtil.isPlateSchema(schemaId)) {
    return ColumnDataType.PLATE;
  } else if (APIUtil.isBoxSchema(schemaId)) {
    return ColumnDataType.BOX;
  } else if (APIUtil.isLocationSchema(schemaId)) {
    return ColumnDataType.LOCATION;
  }
  // Shouldn't actually be possible, just to satisfy the types
  return ColumnDataType.STORABLE;
}

/**
 * Given a column's definition, guess what type of data it is likely to contain.
 * If possible, provide the full structured table to include additional context to help guess. (e.g. for lookup columns)
 * Returns null if no useful data type is detected.
 * Returns undefined if we cannot resolve the data type without more context or data loaded.
 * (Lookups need table and schema information to resolve)
 */
export function guessColumnDataType(
  columnDefinition: ColumnDefinition,
  // We don't need the full StructuredTable.
  structuredTable?: {
    schemas: Array<StructuredTableSchema>;
  }
): ColumnDataTypeSpec | undefined | null {
  const tableColumnType = columnDefinition.type;
  switch (tableColumnType) {
    case TableColumnType.BOOLEAN: {
      return {type: ColumnDataType.BOOLEAN};
    }
    case TableColumnType.ALIAS:
    case TableColumnType.BIOENTITY_NAME:
    case TableColumnType.STRING:
    case TableColumnType.COMPLEX_POLYMER_CONNECTIONS: {
      return {type: ColumnDataType.TEXT};
    }
    case TableColumnType.NUMBER_OF_CONTAINERS:
    case TableColumnType.OLIGO_DUPLEX_OFFSET: {
      return {type: ColumnDataType.INTEGER};
    }
    case TableColumnType.CONCENTRATION:
    case TableColumnType.MEASUREMENT_AMOUNT:
    case TableColumnType.QUANTITY: {
      return {type: ColumnDataType.DECIMAL};
    }
    case TableColumnType.CONTAINERS: {
      return {
        type: ColumnDataType.CONTAINER,
        schemaId: columnDefinition.containerSchemaId || null,
      };
    }
    case TableColumnType.PARENT_STORABLE: {
      return {
        type:
          columnDefinition.parentStorableType === ParentStorableType.LOCATION
            ? ColumnDataType.LOCATION
            : ColumnDataType.STORABLE,
        schemaId: null,
        isMulti: false,
      };
    }
    case TableColumnType.PLATE: {
      // Column definition could be storing a container schema id, which we don't want
      const plateSchemaId =
        columnDefinition.plateSchemaId != null && APIUtil.isPlateSchema(columnDefinition.plateSchemaId)
          ? columnDefinition.plateSchemaId
          : null;
      return {
        type: ColumnDataType.PLATE,
        schemaId: plateSchemaId,
        isMulti: false,
      };
    }
    case TableColumnType.BOX: {
      // Column definition could be storing a container schema id, which we don't want
      const boxSchemaId =
        columnDefinition.boxSchemaId != null && APIUtil.isBoxSchema(columnDefinition.boxSchemaId)
          ? columnDefinition.boxSchemaId
          : null;
      return {
        type: ColumnDataType.BOX,
        schemaId: boxSchemaId,
        isMulti: false,
      };
    }
    case TableColumnType.EXISTING_BIOENTITY:
      return {
        type: ColumnDataType.ENTITY,
        schemaId:
          columnDefinition.tagSchemaOrInterface === 'ALL' ? null : columnDefinition.tagSchemaOrInterface.id,
        isMulti: false,
      };
    case TableColumnType.BIOENTITY_SELECTOR: {
      const schemaFieldForFiltering = columnDefinition.schemaFieldForFiltering;
      const filteringRequiredLink = schemaFieldForFiltering?.requiredLink;
      return {
        type: ColumnDataType.ENTITY,
        schemaId: schemaFieldForFiltering ? getRequiredLinkTagSchema(schemaFieldForFiltering)?.id : null,
        schemaInterfaceId:
          filteringRequiredLink && isRequiredLinkCustomSchemaInterface(filteringRequiredLink)
            ? filteringRequiredLink.schemaInterface.id
            : null,
        isMulti: Boolean(schemaFieldForFiltering?.isMulti),
      };
    }
    case TableColumnType.CUSTOM: {
      return {
        type: CustomColumnDataTypeToColumnDataType[columnDefinition.customColumnConfig.dataType],
      };
    }
    case TableColumnType.TAG_VALUE: {
      const schemaField = columnDefinition.schemaField;
      const isMulti = schemaField?.isMulti ?? false;
      const fieldType = schemaField?.fieldType;
      switch (fieldType) {
        case FieldType.TEXT:
        case FieldType.LONG_TEXT:
        case FieldType.JSON:
          return {type: ColumnDataType.TEXT};
        case FieldType.INTEGER:
          return {type: ColumnDataType.INTEGER};
        case FieldType.FLOAT:
          return {type: ColumnDataType.DECIMAL};
        case FieldType.BOOLEAN:
          return {type: ColumnDataType.BOOLEAN};
        case FieldType.DATE:
          return {type: ColumnDataType.DATE};
        case FieldType.DATETIME:
          return {type: ColumnDataType.DATETIME};
        case FieldType.ENTRY_LINK:
          return {type: ColumnDataType.ENTRY, isMulti};
        case FieldType.BLOB_LINK:
          return {type: ColumnDataType.ATTACHMENT, isMulti};
        // match isBioentityLinkFieldType
        case FieldType.BIOENTITY_LINK:
        case FieldType.PART_LINK:
        case FieldType.TRANSLATION_LINK:
        case FieldType.TRANSCRIPTION_LINK:
        case FieldType.AA_PART_LINK:
        case FieldType.BASE_MOLECULE_LINK:
          return {
            type: ColumnDataType.ENTITY,
            schemaId:
              schemaField?.requiredLink && isRequiredLinkTagSchema(schemaField.requiredLink)
                ? schemaField.requiredLink?.tagSchema?.id
                : null,
            schemaInterfaceId:
              schemaField?.requiredLink && isRequiredLinkCustomSchemaInterface(schemaField.requiredLink)
                ? schemaField.requiredLink.schemaInterface.id
                : null,
            isMulti,
          };
        // match isStorableLinkFieldType
        case FieldType.STORABLE_LINK: {
          const storableSchemaId =
            columnDefinition.schemaField?.requiredLink &&
            isRequiredLinkStorableSchema(columnDefinition.schemaField?.requiredLink)
              ? columnDefinition.schemaField.requiredLink?.storableSchema?.id
              : null;

          const columnType = getStorableLinkColumnTypeFromSchema(storableSchemaId);
          return {
            type: columnType,
            schemaId: storableSchemaId,
            isMulti,
          };
        }
        case FieldType.SELECTOR:
          return {type: ColumnDataType.DROPDOWN, schemaId: schemaField.schemaFieldSelectorId, isMulti};
        // Unsupported schema types
        case FieldType.ASSAY_REQUEST_LINK:
        case FieldType.ASSAY_RESULT_LINK:
        case FieldType.ASSAY_RUN_LINK:
        case FieldType.BATCH_LINK:
        case FieldType.SAMPLE_LINK:
          return null;
        default:
          // if this check is failing, the FieldType must be added to a case above!
          assertNever(fieldType);
      }
      return null;
    }
    case TableColumnType.DEDICATED_LOOKUP: {
      if (!BenchlingConfig.ELN_ENABLE_LOOKUP_TABLE_MAPPING) {
        return null;
      }
      // inferring the lookup type requires having the table's starting schema
      if (!structuredTable?.schemas[0]) {
        return undefined;
      }
      let inferredType: InferredLookupConfigType;
      try {
        inferredType = inferTypeFromLookupConfig(columnDefinition.lookupConfig, structuredTable.schemas[0]);
      } catch (e) {
        if (e instanceof LookupSchemaNotFoundError) {
          // If we error here, this usually means the schemas are not loaded yet and we cannot resolve the value
          // It may still have a type so this is not null
          return undefined;
        } else if (e instanceof LookupSchemaFieldNotFoundError) {
          // If we are missing a particular schema field in the lookup, that indicates a broken lookup,
          // so we should consider this not a valid type (and avoid erroring completely)
          return null;
        } else {
          throw e;
        }
      }
      if (isBenchlingTypeLeafType(inferredType.typeDef)) {
        const dataType = LEAF_TYPE_TO_COLUMN_DATA_TYPE[inferredType.typeDef];
        if (dataType) {
          return {
            type: dataType,
            schemaId: inferredType.linkSchema?.id ?? null,
            isMulti: inferredType.isMultiplex,
          };
        }
      }
      return null;
    }
    case TableColumnType.ROW_DELEGATE: {
      return {type: ColumnDataType.ROW_DELEGATE};
    }
    case TableColumnType.PROCESS_TASK:
      return {type: ColumnDataType.PROCESS_TASK};
    case TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE:
      return {type: ColumnDataType.DATE};
    // BULK ASSEMBLY COLUMNS
    case TableColumnType.BULK_ASSEMBLY_FRAGMENT_POLYMER:
    case TableColumnType.BULK_ASSEMBLY_BIN_SELECTOR:
    case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_NAME:
    case TableColumnType.BULK_ASSEMBLY_VALIDATION_STATUS:
    case TableColumnType.BULK_ASSEMBLY_PRIMER:
    case TableColumnType.BULK_ASSEMBLY_PRIMER_BASES:
    case TableColumnType.BULK_ASSEMBLY_CONSTRUCT_FRAGMENT_SELECTOR:
    case TableColumnType.BULK_ASSEMBLY_PREFERRED_PRIMER:
    case TableColumnType.BULK_ASSEMBLY_RESTRICTION_ENZYME:
      return {type: ColumnDataType.TEXT};
    // The selected sites are serialized as text.
    case TableColumnType.ADC_CONJUGATION_SITES:
    case TableColumnType.ADC_LINKER_PAYLOAD_R_GROUP:
      return {type: ColumnDataType.TEXT};
    case TableColumnType.BULK_ASSEMBLY_FRAGMENT_ENDPOINT:
    case TableColumnType.BULK_ASSEMBLY_LENGTH:
      return {type: ColumnDataType.INTEGER};
    // Unsupported Columns
    case TableColumnType.BATCH:
    case TableColumnType.DNA_BASES:
    case TableColumnType.RNA_BASES:
    case TableColumnType.DNA_HELM:
    case TableColumnType.RNA_HELM:
    case TableColumnType.DNA_IDT:
    case TableColumnType.RNA_IDT:
    case TableColumnType.BASES:
    case TableColumnType.SEQUENCE_TOPOLOGY:
    case TableColumnType.SMILES:
    case TableColumnType.STAGE_RUN:
    case TableColumnType.POSITION:
    case TableColumnType.SAMPLE:
    case TableColumnType.PARTIES:
    case TableColumnType.SAMPLE_RESTRICTION_STATUS:
    case TableColumnType.PROCESS_TASK_ASSIGNEE:
    case TableColumnType.PROCESS_TASK_STATUS:
    case TableColumnType.PROCESS_OUTPUT:
    case TableColumnType.PROCEDURE:
    case TableColumnType.PROCEDURE_PARAMETER_CONFIRMED_IN:
    case TableColumnType.PROCEDURE_PARAMETER_MIN_MAX:
    case TableColumnType.PROCEDURE_SCHEMA_FIELD_TYPE:
    case TableColumnType.PROCEDURE_CONDITION_REPLICATES:
    case TableColumnType.INGREDIENT_CATALOG_ID:
    case TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT:
    case TableColumnType.INGREDIENT_NOTES:
    case TableColumnType.ANONYMOUS_TAG_VALUE:
    case TableColumnType.EXPERIMENTAL_ROLE:
    case TableColumnType.FILL_PLATE_MEASUREMENT_AMOUNT:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CHIP:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_CONTAINER_CHIP:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_ICON:
    case TableColumnType.FILL_PLATE_TRANSFER_SOURCE_SCHEMA_CHIP:
    case TableColumnType.PLATE_MAP_MATERIAL_INPUT:
    case TableColumnType.UNIT_SELECTOR:
    case TableColumnType.UNIT_REFERENCE:
    case TableColumnType.STUDIES:
    case TableColumnType.FULFILLABLE_STATUS:
    case TableColumnType.REQUEST_TASK_SELECTION:
    case TableColumnType.SAMPLE_GROUP_SAMPLE:
    case TableColumnType.OPTION_SELECTOR:
    case TableColumnType.OPTION_SEARCH_SELECTOR:
    case TableColumnType.LOOKUP_CONFIG:
    case TableColumnType.PLATE_LAYOUT_SELECTOR:
    case TableColumnType.PROCESS_TASK_EXECUTION_ORIGIN:
    case TableColumnType.MISSING_FIELD:
    case TableColumnType.COMPLEX_POLYMER_COMPONENT:
    case TableColumnType.TEST_DEFINITION:
    case TableColumnType.PROCEDURE_LIBRARY_PARAMETER_SELECTOR:
    case TableColumnType.ANTIBODY_DOMAIN:
    case TableColumnType.MOLECULE_VERSION:
    case TableColumnType.PROTEIN_NESTED_COMPONENT:
      return null;
    default:
      // if this check is failing, the TableColumnType must be added to a case above!
      assertNever(tableColumnType);
  }
  return null;
}

export const LEAF_TYPE_TO_COLUMN_DATA_TYPE: Record<LeafType, ColumnDataType | null> = {
  [LeafType.BOOLEAN]: ColumnDataType.BOOLEAN,
  [LeafType.DATETIME]: ColumnDataType.DATETIME,
  [LeafType.DATE]: ColumnDataType.DATE,
  [LeafType.DECIMAL]: ColumnDataType.DECIMAL,
  [LeafType.INTEGER]: ColumnDataType.INTEGER,
  [LeafType.JSON]: ColumnDataType.TEXT,
  [LeafType.TEXT]: ColumnDataType.TEXT,
  [LeafType.BENCHLING_OBJECT]: null,
  [LeafType.BLOB]: ColumnDataType.ATTACHMENT,
  [LeafType.DROPDOWN_OPTION]: ColumnDataType.DROPDOWN,
  [LeafType.BATCH]: null,
  [LeafType.CHART]: null,
  [LeafType.DATA_FRAME]: null,
  [LeafType.DATASET]: null,
  [LeafType.FILE]: null,
  [LeafType.SCRATCH_FILE]: null,
  [LeafType.BIOENTITY]: ColumnDataType.ENTITY,
  [LeafType.BASIC_FOLDER_ITEM]: ColumnDataType.ENTITY,
  [LeafType.MIXTURE]: null,
  [LeafType.MOLECULE]: ColumnDataType.ENTITY,
  [LeafType.PROTEIN]: ColumnDataType.ENTITY,
  [LeafType.SEQUENCE]: ColumnDataType.ENTITY,
  [LeafType.ENTRY]: ColumnDataType.ENTRY,
  [LeafType.PROTOCOL]: null,
  [LeafType.REQUEST]: null,
  [LeafType.RESULT]: null,
  [LeafType.RUN]: null,
  [LeafType.STORABLE]: ColumnDataType.STORABLE,
  [LeafType.BOX]: ColumnDataType.BOX,
  [LeafType.CONTAINER]: ColumnDataType.CONTAINER,
  [LeafType.LOCATION]: ColumnDataType.LOCATION,
  [LeafType.PLATE]: ColumnDataType.PLATE,
  [LeafType.PROCESS]: null,
  [LeafType.PROCESS_TASK]: ColumnDataType.PROCESS_TASK,
  [LeafType.PROCESS_OUTPUT]: null,
  [LeafType.STUDY]: null,
  [LeafType.TEAM]: null,
  [LeafType.TEST_ORDER]: null,
  [LeafType.USER]: null,
  [LeafType.WORKSHEET]: null,
};

const CustomColumnDataTypeToColumnDataType: Record<CustomColumnDataType, ColumnDataType> = {
  [CustomColumnDataType.BOOLEAN]: ColumnDataType.BOOLEAN,
  [CustomColumnDataType.INTEGER]: ColumnDataType.INTEGER,
  [CustomColumnDataType.FLOAT]: ColumnDataType.DECIMAL,
  [CustomColumnDataType.TEXT]: ColumnDataType.TEXT,
  [CustomColumnDataType.DATE]: ColumnDataType.DATE,
  [CustomColumnDataType.DATETIME]: ColumnDataType.DATETIME,
};

/** A map of usable exceptions for compatibility for types that are easily converted to each other or one is
 * a subset of another.
 * Access like ColumnDataTypeCompatibilityExceptions[sourceType]?.[destinationType] */
const ColumnDataTypeCompatibilityExceptions: {
  [sourceType in ColumnDataType]?: {[destinationType in ColumnDataType]?: true};
} = {
  [ColumnDataType.INTEGER]: {
    [ColumnDataType.DECIMAL]: true,
  },
  [ColumnDataType.DATE]: {
    [ColumnDataType.DATETIME]: true,
  },
  // Any specific inventory columns can go into a generic "storable" (aka any inventory) column
  // Generic any storable columns can go into all specific inventory columns
  [ColumnDataType.LOCATION]: {
    [ColumnDataType.STORABLE]: true,
  },
  [ColumnDataType.BOX]: {
    [ColumnDataType.STORABLE]: true,
  },
  [ColumnDataType.PLATE]: {
    [ColumnDataType.STORABLE]: true,
  },
  [ColumnDataType.CONTAINER]: {
    [ColumnDataType.STORABLE]: true,
  },
  [ColumnDataType.STORABLE]: {
    [ColumnDataType.CONTAINER]: true,
    [ColumnDataType.BOX]: true,
    [ColumnDataType.PLATE]: true,
    [ColumnDataType.LOCATION]: true,
  },
};

/** Determine if a source column can be mapped to a destination column based on the types. */
export function areColumnDataTypesCompatible(
  sourceColumnDataType: ColumnDataTypeSpec,
  destinationColumnDataType: ColumnDataTypeSpec
): boolean {
  // the columns should have the same data types, or be one of a few known exceptions (ex: integer -> decimal)
  const typesCompatible =
    sourceColumnDataType.type === destinationColumnDataType.type ||
    Boolean(
      ColumnDataTypeCompatibilityExceptions[sourceColumnDataType.type]?.[destinationColumnDataType.type]
    );
  // either the schemas are the same or one of them is not specified
  // (the any -> specific schema case has the potential for different types, but users like the flexibility)
  const schemasCompatible = Boolean(
    sourceColumnDataType.schemaId == null ||
    destinationColumnDataType.schemaId == null ||
    sourceColumnDataType.schemaId === destinationColumnDataType.schemaId
  );
  // For fieldset (schema interface) constraints, require an exact match when both sides specify one.
  // Cross-type (schema <-> fieldset membership) remains null-tolerant for now, matching the existing
  // schema behavior above.
  const schemaInterfacesCompatible = Boolean(
    sourceColumnDataType.schemaInterfaceId == null ||
    destinationColumnDataType.schemaInterfaceId == null ||
    sourceColumnDataType.schemaInterfaceId === destinationColumnDataType.schemaInterfaceId
  );
  // either the isMulti-ness is the same or only the destination isMulti.
  // (only disallow multi -> single)
  const isMultiCompatible = Boolean(destinationColumnDataType.isMulti || !sourceColumnDataType.isMulti);

  return typesCompatible && schemasCompatible && schemaInterfacesCompatible && isMultiCompatible;
}
